import { FC, useState, useEffect, useMemo } from 'react'
import { Breadcrumbs, Input } from '@mondra/ui-components'
import { GET_ALL_WATER_SCARCITY } from '../../../query/water-scarcity'
import { useQuery } from '@apollo/client'
import { IWaterScarcity } from './types'
import { getWaterScarcityVars } from './helpers'
import { filterEnums, getTableData } from '../../../shared/gridFunctions'
import Table from '../../../components/Table'

import './index.scss'
import { ExportGraphqlButton } from '../../../components/ExportGraphqlButton'
import { EXPORT_WATER_SCARCITY_QUERY } from '../../../query/export-water-scarcity'
import { EXPORT_MAX_ROW_COUNT } from '../../../const'

const WaterScarcity: FC = () => {
  const [tableData, setTableData] = useState<IWaterScarcity[]>([])
  const [columnDefs, setColumn] = useState([])
  const [tableSearch, setTableSearch] = useState<string | null>(null)
  const [debouncedTableSearch, setDebouncedTableSearch] = useState(tableSearch)
  const [totalCount, setTotalCount] = useState<string>('')
  const [endCursor, setEndCursor] = useState<string | null>(null)
  const [startCursor, setStartCursor] = useState<string | null>(null)
  const [waterScarcityVars, setWaterScarcityVars] = useState<any>(getWaterScarcityVars)
  const [order, setOrder] = useState([{ id: 'ASC' }])

  const { data, loading, error, refetch } = useQuery(GET_ALL_WATER_SCARCITY, {
    variables: {
      str: '',
      first: 100,
      last: null,
      order: order,
      ...getWaterScarcityVars,
    },
  })

  const anyRefetch = (variables?: any): Promise<any> => {
    return refetch(variables)
  }

  const setCursors = () => {
    setEndCursor(data.waterScarcity.pageInfo.endCursor)
    setStartCursor(data.waterScarcity.pageInfo.startCursor)
  }

  useEffect(() => {
    const timer = setTimeout(() => setTableSearch(debouncedTableSearch), 1000)
    return () => clearTimeout(timer)
  }, [debouncedTableSearch])

  useEffect(() => {
    refetch()
    getTableData(
      data,
      loading,
      setTableData,
      setColumn,
      setTotalCount,
      setCursors,
      '',
      'waterScarcity',
      'waterScarcityfields'
    )
  }, [data])

  useEffect(() => {
    if (tableSearch != null || !order[0].id) {
      if (!loading) {
        setWaterScarcityVars({ ...filterEnums(tableSearch, getWaterScarcityVars, data) })
        anyRefetch({
          str: tableSearch || '',
          ...filterEnums(tableSearch, getWaterScarcityVars, data),
          first: 100,
          after: null,
          before: null,
          last: null,
          order: order,
        })
      }
    }
  }, [tableSearch, order, loading])

  const exportPayload = useMemo(
    () => ({
      operationName: 'WaterScarcity',
      query: EXPORT_WATER_SCARCITY_QUERY.loc?.source.body,
      variables: {
        first: EXPORT_MAX_ROW_COUNT,
        after: null,
        where: null,
        order,
      },
    }),
    [order]
  )

  return (
    <div className={'packaging'}>
      <div style={{ width: '50rem' }} className={'mb-4'}>
        <p className={'mt-3 decoration-gray-600'}>
          <Breadcrumbs routes={[{ path: '/water-scarcity', title: 'Water Scarcity Table' }]} />
        </p>
        <h2 className={'mb-5 mt-3 text-2xl'}>Water Scarcity</h2>
        <div className="packaging-actions items-center">
          <Input
            onChange={e => setDebouncedTableSearch(e.target.value)}
            iconType="search"
            placeholder="Search"
          />
          <ExportGraphqlButton payload={exportPayload} />
        </div>
      </div>
      <Table
        loading={loading}
        tableData={tableData}
        columnDefs={columnDefs}
        totalNumber={totalCount}
        endCursor={endCursor}
        startCursor={startCursor}
        refetch={refetch}
        defaultVars={waterScarcityVars || getWaterScarcityVars}
        setCursors={setCursors}
        tableSearch={tableSearch}
        setOrder={setOrder}
        order={order}
        suppressRowClickSelection
      />
    </div>
  )
}

export default WaterScarcity
